import {
  envOverrides,
  getDefaultEnvName,
  getOverride,
  getUnconfiguredApiUrl,
} from '@app/env-helpers';

import { inline_ } from './shared/utils/string.utils';

class EnvService {
  private storageKey :string = `environment_${location.origin}`;
  #initPromise :Promise<void> | null = null;
  #url :URL;
  #urlV2 :URL;
  #IsLocal = { BACKEND: false, FRONTEND: false };

  public tenantId :string = '';
  public tenantLocalToggles :string = '';
  public url :string = '';
  public urlV2 :string = '';
  public logRocketAppId :string = '';
  public authTokenKey :string = 'authce9d77b308c149d5992a80073637e4d5';
  public accessTokenKey :string = 'authce9d77b308c149d5992a80073637e4d5';
  public refreshTokenKey :string = 'authre9d77b308c149d5992a80073637e4d5';
  public versionCheckURL :string = '';
  public clientConfig :Record<string, string>;
  public ECHO_CONFIG :Record<string, any>;

  constructor() {
    this.getLocalEnvironment();
  }

  private setLocalEnvironment = () => {
    localStorage.setItem(
      this.storageKey,
      JSON.stringify({
        url: this.url,
        tenantId: this.tenantId,
        tenantLocalToggles: this.tenantLocalToggles,
        urlV2: this.urlV2,
        logRocketAppId: this.logRocketAppId,
        authTokenKey: this.authTokenKey,
        accessTokenKey: this.accessTokenKey,
        refreshTokenKey: this.refreshTokenKey,
        versionCheckURL: this.versionCheckURL,
        clientConfig: this.clientConfig,
        ECHO_CONFIG: this.ECHO_CONFIG,
      }),
    );
  };

  private getLocalEnvironment = () => {
    const lsEnv = localStorage.getItem(this.storageKey);
    if (lsEnv) {
      try {
        const env = JSON.parse(lsEnv);
        this.setEnvironment(env);
      } catch (e) {}
    }
  };

  init = async () :Promise<void> => {
    if (this.#initPromise) {
      return this.#initPromise;
    }

    this.#initPromise = this.#_init();

    return this.#initPromise;
  };

  #_init = async () :Promise<void> => {
    const localEnv = getOverride().environment;
    const envNameOverride = localEnv?.env;
    const envNameDefault = getDefaultEnvName();
    const defaultEnv = envOverrides[envNameDefault];
    let env;

    if (envNameOverride && envNameDefault !== envNameOverride) {
      env = envOverrides[envNameOverride];
    } else {
      const envInfoUrl =
        getUnconfiguredApiUrl() || defaultEnv.url.replace('/admin', '');
      const res = await fetch(`${envInfoUrl}/environment_information`, {
        headers: new Headers({ 'Content-Type': 'application/json' }),
      });
      env = await res.json();
    }

    this.setEnvironment({
      ...env,
      url: localEnv?.url || env.url,
      urlV2: localEnv?.urlV2 || env.urlV2,
    });
    this.setLocalEnvironment();
  };

  isLocal() :boolean {
    return this.#IsLocal.BACKEND || this.#IsLocal.FRONTEND;
  }
  isLocalBackend() :boolean {
    return this.#IsLocal.BACKEND;
  }
  isLocalFrontend() :boolean {
    return this.#IsLocal.FRONTEND;
  }

  /**
   * Returns a human-readable summary of the environment setup.  If the front- or
   * back-end is using a local environment, that will be represented in the summary.
   * Otherwise, the summary will just be the name of the client.
   *
   * @returns {string} The human-readable summary of the environment setup.
   */
  getHumanSummary() :string {
    let summary = this.clientConfig.name;

    if (!this.isLocal()) {
      return summary;
    }

    if (this.isLocalFrontend()) {
      summary = location.hostname;
    }
    if (this.#url.hostname !== location.hostname) {
      summary += ` => ${this.#url.hostname.replace('.xjimports.com', '')}`;
    }

    return summary;
  }

  setEnvironment = (env :{
    tenantId :string;
    tenantLocalToggles :string;
    url :string;
    urlV2 :string;
    logRocketAppId :string;
    versionCheckURL :string;
    clientConfig :Record<string, string>;
    ECHO_CONFIG :Record<string, any>;
  }) :void => {
    this.tenantId = env.tenantId ? env.tenantId : '';
    this.tenantLocalToggles = env.tenantLocalToggles;
    this.url = env.url;
    this.urlV2 = env.urlV2;
    this.logRocketAppId = env.logRocketAppId;
    this.clientConfig = env.clientConfig;
    this.versionCheckURL = env.versionCheckURL;
    this.ECHO_CONFIG = env.ECHO_CONFIG;

    this.#updateURLObjects();
    this.#updateIsLocal();
  };

  #updateIsLocal() {
    this.#IsLocal.BACKEND =
      this.#url.port === '8000' && this.clientConfig.name === 'Badabing';
    this.#IsLocal.FRONTEND =
      location?.port === '4003' && this.clientConfig.name === 'Badabing';
  }

  #updateURLObjects() {
    try {
      this.#url = new URL(this.url);
    } catch (e) {
      console.warn(
        inline_`WARNING: 'url' is not a valid URL (${JSON.stringify(this.url)})`,
      );
      this.#url = new URL('https://invalid_url');
    }

    try {
      this.#urlV2 = new URL(this.urlV2);
    } catch (e) {
      console.error(
        inline_`ERROR: 'urlV2' is not a valid URL (${JSON.stringify(this.urlV2)})`,
      );
      this.#urlV2 = new URL('https://invalid_url');
    }

    if (this.#url.origin !== this.#urlV2.origin) {
      console.warn(
        inline_`WARNING: 'url' and 'urlV2' origins do not match (${this.#url.origin}
        !== ${this.#urlV2.origin})`,
      );
    }
  }
}

export const environment = new EnvService();
